import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Grid2 as Grid,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FormProvider, useForm } from "react-hook-form";
import { useRef, useMemo, useState } from "react";
import { useNotification } from "@refinedev/core";
import { Form, CsvExportButton, ModalRef } from "@shared/components";
import { getAgreementsService } from "@modules/agreements/services/queries/listAgreements/service";
import { useAgreementErpPlansQuery } from "@modules/integration/services/queries/getAgreementErpPlans";
import { syncErpPlansReportService } from "@modules/integration/services/syncErpPlansReport";
import { getAllMapIntegrationPlansService } from "@modules/mapIntegrationPlans/services/queries/listMapIntegrationPlans/service";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MapIntegrationPlan } from "@modules/mapIntegrationPlans/types";
import { BulkMapIntegrationPlanModal, CreateMapIntegrationPlanModal } from "@modules/mapIntegrationPlans/components";
import { AgreementErpPlan } from "@modules/integration/services/queries/getAgreementErpPlans/types";
import { CheckCircle, HighlightOff, Sync } from "@mui/icons-material";
import { DeleteButton } from "@refinedev/mui";
import { useFetchResourcesByIdsQuery } from "@shared/services/queries";
import { ApiResponse } from "@shared/types";
import { Plan } from "@modules/plans/types";

interface PlanMappingRow extends AgreementErpPlan {
  isMapped: boolean;
  /** Todos os mapeamentos que batem com esse plano ERP */
  mappings: MapIntegrationPlan[];
}

/**
 * Encontra todos os mapeamentos que correspondem ao plano ERP.
 * Compara slug (ERP) com plan_slug_integration (mapeamento).
 * Ignora type_mapping - sempre agrupa por slug.
 */
function findErpPlanMappings(
  erpPlan: AgreementErpPlan,
  allMappings: MapIntegrationPlan[]
): MapIntegrationPlan[] {
  return allMappings.filter((m) => m.plan_slug_integration === erpPlan.slug);
}

export function IntegrationPlanMapping() {
  const theme = useTheme();
  const { open: openNotification } = useNotification();
  const bulkModalRef = useRef<ModalRef>(null);
  const createModalRef = useRef<ModalRef>(null);
  const [syncingReport, setSyncingReport] = useState(false);

  const formMethods = useForm<{ agreement: number | null }>({
    defaultValues: { agreement: null },
  });

  const agreementId = formMethods.watch("agreement");
  const [search, setSearch] = useState("");
  type FilterStatus = "all" | "mapped" | "unmapped";
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");

  const {
    data: erpData,
    isLoading: loadingErp,
    refetch: refetchErpPlans,
    isFetching: isSyncing,
  } = useAgreementErpPlansQuery(
    agreementId != null ? String(agreementId) : null
  );
  const erpPlans = erpData?.data ?? [];
  const syncReport = erpData?.report ?? null;

  const queryClient = useQueryClient();

  // Buscar TODOS os mapeamentos do convênio (todas as páginas)
  const {
    data: mappings = [],
    isLoading: loadingMappings,
    refetch: refetchMappings,
  } = useQuery({
    queryKey: ["subscription_mapintegrationplan_admin", "agreement", "all", agreementId],
    queryFn: () => getAllMapIntegrationPlansService(String(agreementId)),
    enabled: !!agreementId,
  });

  // Extrair IDs únicos dos planos dos mapeamentos
  const plansIds = useMemo(
    () => [...new Set(mappings.map((m) => m.plan).filter((id): id is number => !!id))],
    [mappings]
  );

  // Buscar detalhes dos planos
  const { data: plansData } = useFetchResourcesByIdsQuery<ApiResponse<Plan>>({
    resource: "v1/subscription_plan_admin",
    ids: plansIds,
  });

  // Criar mapa de planos para acesso rápido
  const plansMap = useMemo(() => {
    const map = new Map<number, Plan>();
    plansData?.results?.forEach((plan) => {
      map.set(plan.id, plan);
    });
    return map;
  }, [plansData]);

  const rows: PlanMappingRow[] = useMemo(() => {
    return erpPlans.map((erp) => {
      const planMappings = findErpPlanMappings(erp, mappings);
      return { ...erp, isMapped: planMappings.length > 0, mappings: planMappings };
    });
  }, [erpPlans, mappings]);

  // Estatísticas (totais) por status de mapeamento
  const stats = useMemo(() => {
    const mapped = rows.filter((r) => r.isMapped).length;
    const unmapped = rows.filter((r) => !r.isMapped).length;
    return { total: rows.length, mapped, unmapped };
  }, [rows]);

  const formatSyncDate = (iso: string | undefined): string => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      const h = String(d.getHours()).padStart(2, "0");
      const m = String(d.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${h}:${m}`;
    } catch {
      return "";
    }
  };

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = rows;
    if (term) {
      list = list.filter(
        (row) =>
          (row.name_plan ?? "").toLowerCase().includes(term) ||
          (row.slug ?? "").toLowerCase().includes(term) ||
          (row.type_mapping ?? "").toLowerCase().includes(term) ||
          (row.erp ?? "").toLowerCase().includes(term) ||
          (row.value != null && String(row.value).toLowerCase().includes(term)) ||
          (row.status ?? "").toLowerCase().includes(term)
      );
    }
    if (filterStatus === "mapped") list = list.filter((r) => r.isMapped);
    if (filterStatus === "unmapped") list = list.filter((r) => !r.isMapped);
    return list;
  }, [rows, search, filterStatus]);

  const handleOpenBulkModal = () => {
    bulkModalRef.current?.open();
  };

  const handleOpenCreateModal = (erpPlan: AgreementErpPlan) => {
    createModalRef.current?.open({
      plan_slug_integration: erpPlan.slug,
    });
  };

  const handleSyncErpReport = async () => {
    if (agreementId == null) return;
    setSyncingReport(true);
    try {
      await syncErpPlansReportService(String(agreementId));
      openNotification?.({
        type: "success",
        message: "Sincronização dos planos ERP disparada. Aguarde alguns instantes e clique em Sincronizar para atualizar.",
      });
      await refetchErpPlans();
    } catch (err: any) {
      openNotification?.({
        type: "error",
        message: err?.response?.data?.error ?? err?.message ?? "Erro ao sincronizar planos ERP.",
      });
    } finally {
      setSyncingReport(false);
    }
  };

  const loading = loadingErp || loadingMappings;
  const hasAgreement = agreementId != null;

  const dataToExport = useMemo(
    () =>
      filteredRows.map((row) => ({
        Mapeado: row.isMapped ? "Sim" : "Não",
        "Qtd Mapeamentos": row.mappings.length,
        Tipo: row.type_mapping ?? "—",
        "Nome (ERP)": row.name_plan,
        "Slug (ERP)": row.slug,
        "Preço (ERP)":
          row.price != null
            ? `R$ ${row.price.toFixed(2).replace(".", ",")}`
            : "—",
        ERP: row.erp ?? "—",
        Clientes: row.count_clients ?? "—",
        "Clientes ativos": row.count_clients_active ?? "—",
        Status: row.isMapped ? "Já mapeado" : "Não mapeado",
      })),
    [filteredRows]
  );

  // Helper para obter nome do plano
  const getPlanName = (planId: number): string => {
    const plan = plansMap.get(planId);
    return plan?.display_name || plan?.name || `Plano #${planId}`;
  };

  return (
    <Grid container width={1} spacing={2}>
      <Paper
        sx={{
          padding: "24px",
          width: "100%",
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.background.paper
              : theme.palette.background.paper,
        }}
      >
        <Stack direction="column" spacing={3}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{ color: theme.palette.text.primary }}
          >
            Comparar planos ERP com mapeamentos
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem", mb: 1.5 }}>
            Selecione um convênio para carregar os planos do ERP e verificar
            quais já possuem mapeamento. Expanda a linha para ver os planos já
            cadastrados. Use &quot;Mapear&quot; para adicionar um novo mapeamento.
          </Typography>

          <FormProvider {...formMethods}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid size={{ xs: 12, md: 6 }}>
                <Form.Autocomplete
                  name="agreement"
                  label="Convênio"
                  queryHook={getAgreementsService}
                  required
                  fullWidth
                />
              </Grid>
            </Grid>
          </FormProvider>

          {hasAgreement && (
            <Box>
              {loading ? (
                <Stack alignItems="center" spacing={2} py={4}>
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary">
                    {loadingErp
                      ? "Buscando planos no ERP..."
                      : "Buscando mapeamento..."}
                  </Typography>
                </Stack>
              ) : (
                <>
                  {erpPlans.length > 0 && (
                    <>
                      <Stack
                        direction="column"
                        spacing={1.5}
                        sx={{
                          mb: 2,
                          p: 2,
                          bgcolor: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[50],
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                            Tipo de mapeamento:{" "}
                            <strong>{(erpPlans[0] as AgreementErpPlan).type_mapping ?? "—"}</strong>
                            {" "}• Total de mapeamentos: <strong>{mappings.length}</strong>
                            {syncReport && (
                              <> • Sincronizado em <strong>{formatSyncDate(syncReport.updated_at ?? syncReport.created_at)}</strong></>
                            )}
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Sync sx={{ fontSize: "1rem" }} />}
                            onClick={() => refetchErpPlans()}
                            disabled={isSyncing || !agreementId}
                            sx={{ fontSize: "0.75rem", minHeight: "28px", py: 0.5 }}
                          >
                            {isSyncing ? "Sincronizando..." : "Sincronizar"}
                          </Button>
                          <Button
                            variant="text"
                            size="small"
                            onClick={handleSyncErpReport}
                            disabled={syncingReport || !agreementId}
                            sx={{ fontSize: "0.7rem", minHeight: "28px", py: 0.5, color: "text.secondary" }}
                          >
                            {syncingReport ? "Disparando sync..." : "Disparar sync no ERP"}
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleOpenBulkModal}
                            disabled={!agreementId || erpPlans.length === 0}
                            sx={{ fontSize: "0.75rem", minHeight: "28px", py: 0.5 }}
                          >
                            Mapeamento em massa
                          </Button>
                          <CsvExportButton
                            data={dataToExport}
                            filename="mapeamento-planos-erp.csv"
                            isDisabled={loading || filteredRows.length === 0}
                          />
                        </Stack>
                      </Stack>
                      <TextField
                        placeholder="Buscar por nome, tipo ou ERP..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{ mb: 2 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                        <Chip
                          label={`Todos (${stats.total})`}
                          onClick={() => setFilterStatus("all")}
                          color={filterStatus === "all" ? "primary" : "default"}
                          variant={filterStatus === "all" ? "filled" : "outlined"}
                          size="small"
                          sx={{ cursor: "pointer", fontSize: "0.75rem", height: "24px" }}
                        />
                        <Chip
                          icon={<CheckCircle sx={{ fontSize: "0.875rem" }} />}
                          label={`Mapeado (${stats.mapped})`}
                          onClick={() => setFilterStatus("mapped")}
                          color={filterStatus === "mapped" ? "success" : "default"}
                          variant={filterStatus === "mapped" ? "filled" : "outlined"}
                          size="small"
                          sx={{ cursor: "pointer", fontSize: "0.75rem", height: "24px" }}
                        />
                        <Chip
                          icon={<HighlightOff sx={{ fontSize: "0.875rem" }} />}
                          label={`Não mapeado (${stats.unmapped})`}
                          onClick={() => setFilterStatus("unmapped")}
                          color={filterStatus === "unmapped" ? "default" : "default"}
                          variant={filterStatus === "unmapped" ? "filled" : "outlined"}
                          size="small"
                          sx={{ cursor: "pointer", fontSize: "0.75rem", height: "24px" }}
                        />
                      </Stack>
                    </>
                  )}

                  {filteredRows.length === 0 ? (
                    <Stack spacing={2} alignItems="center" py={4}>
                      {rows.length === 0 ? (
                        <>
                          <Alert severity="info" sx={{ maxWidth: 560 }}>
                            Nenhum plano ERP retornado para este convênio. Dispare a sincronização no dashboard para carregar os planos.
                          </Alert>
                          <Button
                            variant="contained"
                            startIcon={<Sync />}
                            onClick={handleSyncErpReport}
                            disabled={syncingReport || !agreementId}
                          >
                            {syncingReport ? "Sincronizando..." : "Sincronizar planos ERP"}
                          </Button>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary" align="center">
                          {filterStatus !== "all"
                            ? `Nenhum plano ${filterStatus === "mapped" ? "mapeado" : "não mapeado"} para o filtro selecionado.`
                            : "Nenhum resultado para a busca."}
                        </Typography>
                      )}
                    </Stack>
                  ) : (
                    <Box>
                      {filteredRows.map((row) => (
                        <Accordion
                          key={row.id}
                          sx={{
                            "&:before": { display: "none" },
                            boxShadow: "none",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ minHeight: 56 }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={2}
                              sx={{ width: "100%" }}
                            >
                              {row.isMapped ? (
                                <CheckCircle color="success" titleAccess="Mapeado" />
                              ) : (
                                <HighlightOff color="disabled" titleAccess="Não mapeado" />
                              )}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.name_plan}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {row.slug} • {row.type_mapping ?? "—"} •{" "}
                                  {row.price != null
                                    ? `R$ ${row.price.toFixed(2).replace(".", ",")}`
                                    : "Sem preço"}
                                </Typography>
                              </Box>
                              <Chip
                                label={
                                  row.mappings.length === 0
                                    ? "Não mapeado"
                                    : row.mappings.length === 1
                                    ? "1 mapeamento"
                                    : `${row.mappings.length} mapeamentos`
                                }
                                size="small"
                                color={row.isMapped ? "success" : "default"}
                                variant="outlined"
                              />
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenCreateModal(row);
                                }}
                              >
                                Mapear
                              </Button>
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0 }}>
                            {row.mappings.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                Nenhum plano mapeado para este slug do ERP.
                              </Typography>
                            ) : (
                              <TableContainer>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>ID</TableCell>
                                      <TableCell>Plano</TableCell>
                                      <TableCell>Slug Integração</TableCell>
                                      <TableCell>Ativo</TableCell>
                                      <TableCell>Criado em</TableCell>
                                      <TableCell align="center">Ações</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {row.mappings.map((m) => (
                                      <TableRow key={m.id}>
                                        <TableCell>{m.id}</TableCell>
                                        <TableCell>
                                          <Typography variant="body2">
                                            {getPlanName(m.plan)}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            ID: {m.plan}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>{m.plan_slug_integration}</TableCell>
                                        <TableCell>
                                          <Chip
                                            label={m.active ? "Ativo" : "Inativo"}
                                            size="small"
                                            color={m.active ? "success" : "default"}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          {new Date(m.created_at).toLocaleDateString("pt-BR")}
                                        </TableCell>
                                        <TableCell align="center">
                                          <DeleteButton
                                            hideText
                                            resource="v1/subscription_mapintegrationplan_admin"
                                            recordItemId={m.id}
                                            onSuccess={() => {
                                              queryClient.invalidateQueries({
                                                queryKey: ["subscription_mapintegrationplan_admin"],
                                                refetchType: "all",
                                              });
                                              refetchMappings();
                                            }}
                                          />
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </Stack>
      </Paper>

      <BulkMapIntegrationPlanModal
        modalRef={bulkModalRef}
        agreement={agreementId}
        erpPlans={erpPlans}
        mappings={mappings}
      />
      <CreateMapIntegrationPlanModal modalRef={createModalRef} agreement={agreementId} />
    </Grid>
  );
}
